import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { track } from '../services/analytics';
import { toast } from '../hooks/use-toast';
import { X, Sparkles, MessageCircle, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'rk_lead_captured_at';   // 30-day soft cooldown
const DISMISS_KEY = 'rk_lead_dismissed_at';  // 3-day cooldown on dismiss

const COOLDOWN_MS_AFTER_SUBMIT = 30 * 24 * 60 * 60 * 1000; // 30 days
const COOLDOWN_MS_AFTER_DISMISS = 3 * 24 * 60 * 60 * 1000; // 3 days
const SCROLL_TRIGGER_PERCENT = 0.5;           // fire after 50% scroll
const MIN_TIME_ON_PAGE_MS = 8000;             // don't fire before 8s
const EXIT_ENABLED_AFTER_MS = 15000;          // only arm exit-intent after 15s

/**
 * LeadCaptureModal
 *   - Triggers: scroll, exit-intent, manual (sticky CTA button or `window.dispatchEvent('rk:open-lead')`)
 *   - Submits to /api/demo-requests with the source metadata attached
 *   - Respects 30-day cooldown after success, 3-day after dismiss
 */
function shouldShow() {
  try {
    const submitted = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (submitted && Date.now() - submitted < COOLDOWN_MS_AFTER_SUBMIT) return false;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < COOLDOWN_MS_AFTER_DISMISS) return false;
  } catch (_) {/* ignore private-mode localStorage errors */}
  return true;
}

export default function LeadCaptureModal() {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('scroll_popup');
  const [form, setForm] = useState({ name: '', mobile: '', email: '', beds: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const mountedAt = useRef(Date.now());
  const openedRef = useRef(false); // lock for this session to prevent re-trigger

  const openWith = useCallback((src) => {
    if (openedRef.current) return;
    if (!shouldShow()) return;
    if (Date.now() - mountedAt.current < MIN_TIME_ON_PAGE_MS) return;
    openedRef.current = true;
    setSource(src);
    setOpen(true);
  }, []);

  // === Scroll trigger ===
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = (h.scrollTop || window.scrollY) / Math.max(1, (h.scrollHeight - h.clientHeight));
      if (scrolled >= SCROLL_TRIGGER_PERCENT) openWith('scroll_popup');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [openWith]);

  // === Exit-intent trigger (desktop only — mouseout through top) ===
  useEffect(() => {
    const onMouseOut = (e) => {
      if (Date.now() - mountedAt.current < EXIT_ENABLED_AFTER_MS) return;
      if (!e.relatedTarget && e.clientY <= 0) openWith('exit_popup');
    };
    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [openWith]);

  // === Manual trigger via custom event (from StickyCTA) ===
  useEffect(() => {
    const h = (e) => {
      // Manual opens bypass shouldShow() + mount-time gates
      openedRef.current = true;
      setSource(e?.detail?.source || 'sticky_cta');
      setOpen(true);
    };
    window.addEventListener('rk:open-lead', h);
    return () => window.removeEventListener('rk:open-lead', h);
  }, []);

  // === ESC to close ===
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') closeWith('escape'); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function closeWith(why) {
    setOpen(false);
    if (why !== 'success') {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    }
  }

  const validate = () => {
    const mobileDigits = form.mobile.replace(/\D/g, '');
    if (!form.name || form.name.trim().length < 2) return 'Please enter your name';
    if (mobileDigits.length < 10) return 'Enter a valid 10-digit mobile number';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return 'Enter a valid email (or leave blank)';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast({ title: 'Check your details', description: err, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      // UTM parsing
      const sp = new URLSearchParams(window.location.search);
      const payload = {
        name: form.name.trim(),
        mobile: form.mobile.replace(/\D/g, '').slice(-10),
        email: form.email.trim() || null,
        beds: form.beds ? String(form.beds) : null,
        property_type: null,
        source,
        page_url: window.location.pathname,
        referrer: document.referrer || null,
        utm_source: sp.get('utm_source') || null,
        utm_medium: sp.get('utm_medium') || null,
        utm_campaign: sp.get('utm_campaign') || null,
      };
      await axios.post(`${API}/demo-requests`, payload);
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
      if (window.gtag) {
        window.gtag('event', 'lead_submit', { source, has_email: !!payload.email });
      }
      track('demo_request_submitted', {
        source,
        has_email: !!payload.email,
        beds: payload.beds,
        utm_source: payload.utm_source,
        utm_medium: payload.utm_medium,
        utm_campaign: payload.utm_campaign,
        page_url: payload.page_url,
      });
      setDone(true);
      setTimeout(() => closeWith('success'), 2400);
    } catch (er) {
      toast({ title: 'Something went wrong', description: er.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const headerCopy = {
    scroll_popup: { tag: 'Join the pilot',     title: 'Get early access to RentKollect',       sub: 'Be among the first 50 owners to try it — no sales pitch, just the product.' },
    exit_popup:   { tag: '⏳ Before you go',   title: 'Save hours every week on rent collection', sub: "Drop your details — we'll show you the product live. No sales pitch." },
    sticky_cta:   { tag: 'Book a quick demo',  title: 'See the product in action',               sub: "10-minute WhatsApp walkthrough — no sales pitch, just the product." },
  }[source] || { tag: 'Join the pilot', title: 'Get early access to RentKollect', sub: 'Be among the first 50 owners to try it — no sales pitch, just the product.' };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm" data-testid="lead-capture-modal" onClick={() => closeWith('backdrop')}>
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative animate-[slide-up_0.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={() => closeWith('close_button')}
          data-testid="lead-capture-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="p-10 text-center space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center animate-bounce">
              <Send size={28} className="text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">🎉 You're on the list!</h3>
            <p className="text-gray-600 text-sm">We'll reach out on WhatsApp within 24 hours. Meanwhile, feel free to message us directly at <a href="https://wa.me/919821922296" target="_blank" rel="noopener noreferrer" className="text-emerald-600 font-semibold">+91 98219 22296</a>.</p>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-emerald-500 via-green-600 to-teal-600 text-white p-6 relative overflow-hidden">
              <div className="absolute -top-6 -right-6 text-[90px] opacity-10 select-none pointer-events-none">💬</div>
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur rounded-full px-2.5 py-0.5 text-[11px] font-bold tracking-wide uppercase">
                <Sparkles size={12} /> {headerCopy.tag}
              </div>
              <h2 className="text-2xl font-bold mt-2 leading-tight">{headerCopy.title}</h2>
              <p className="text-emerald-50 text-sm mt-1">{headerCopy.sub}</p>
            </div>

            <form onSubmit={submit} className="p-6 space-y-3" data-testid="lead-capture-form">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Your name</label>
                <input
                  data-testid="lead-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Ashish"
                  className="mt-1 w-full rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 px-4 py-2.5 outline-none"
                  autoFocus
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">WhatsApp number</label>
                <div className="mt-1 flex">
                  <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0 border-gray-300 bg-gray-50 text-gray-600 text-sm">+91</span>
                  <input
                    data-testid="lead-mobile"
                    type="tel"
                    inputMode="numeric"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10-digit mobile"
                    className="flex-1 rounded-r-xl border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 px-4 py-2.5 outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Email (optional)</label>
                <input
                  data-testid="lead-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  className="mt-1 w-full rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 px-4 py-2.5 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Number of beds</label>
                <select
                  data-testid="lead-beds"
                  value={form.beds}
                  onChange={(e) => setForm({ ...form, beds: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 px-4 py-2.5 outline-none bg-white"
                  required
                >
                  <option value="">Select range</option>
                  <option value="1-10">1–10 beds</option>
                  <option value="11-25">11–25 beds</option>
                  <option value="26-50">26–50 beds</option>
                  <option value="51-100">51–100 beds</option>
                  <option value="100+">100+ beds</option>
                </select>
              </div>

              <button
                data-testid="lead-submit"
                type="submit"
                disabled={submitting}
                className="w-full mt-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
              >
                {submitting ? (
                  <><Loader2 size={18} className="animate-spin" /> Submitting…</>
                ) : (
                  <><MessageCircle size={18} /> Show me the product</>
                )}
              </button>

              <p className="text-[11px] text-center text-gray-500 mt-1">
                By submitting, you agree we may WhatsApp you. No spam, ever.
              </p>
            </form>
          </>
        )}
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
